import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, IconButton, Chip } from '@peoplegrove/pg-components';
import { Empty, Spin } from 'antd';
import clsx from 'clsx';
import moment from 'moment';
import { get, isArray } from 'lodash';
import PropTypes from 'prop-types';
import RichText from '../../../../component/RichText';
import uuid from '../../../../utils/uuid';
import useGetConversationDetailsQuery from '../../queries/useGetConversationDetailsQuery';
import useFlagConversation from '../../hooks/useFlagConversation';

import {
  getCleanedMessageForSupportRequestCta,
  messageContainsSupportRequestCta,
} from '../../utils/supportRequestCta.utils';
import './styles/ConversationDetails.scss';

const ConversationDetails = ({ sessionAddress }) => {
  const { flagConversation } = useFlagConversation();

  const handleFlagToggle = (sessionAddr, flagged) => {
    flagConversation({ sessionAddress: sessionAddr, flagged });
  };

  const iconsMap = {
    'pg-thumbs-down-o':
      'https://cdn.peoplegrove.com/admin/1719912601663pg-hand-thumb-down-o.svg',
    'pg-thumbs-down':
      'https://cdn.peoplegrove.com/admin/1719912682708pg-hand-thumb-down.svg',
    'pg-thumbs-up-o':
      'https://cdn.peoplegrove.com/admin/1719911324407pg-hand-thumb-up-o.svg',
    'pg-thumbs-up':
      'https://cdn.peoplegrove.com/admin/1719912494325pg-hand-thumb-up.svg',
  };

  const {
    data: conversation,
    isLoading,
    error,
  } = useGetConversationDetailsQuery({
    sessionAddress,
  });

  if (!sessionAddress) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="conversationDetailsLoading">
        <Spin size="large" />
        <p>Loading conversation details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversationDetailsError">
        <p>Error loading conversation details. Please try again.</p>
      </div>
    );
  }

  if (!conversation) {
    return <Empty description="No conversation selected" />;
  }

  const conversationSummary = get(
    conversation,
    'conversationSummary.conversationSummary',
    ''
  ).trim();

  const ticketURL =
    (conversation.tickets?.length > 1
      ? `https://peoplegrove.zendesk.com/agent/users/${conversation.tickets[0]?.zen_user_id}/requested_tickets`
      : conversation.tickets[0]?.ticketURL) || '';

  const handleOpenZendeskTicket = (ticketURL) => {
    window.open(ticketURL, '_blank');
  };

  const tags = conversation?.tags || [];
  const isConversationFlagged = conversation?.flaggedInternally || false;
  return (
    <div className="conversationDetailsContainer">
      <div className="conversationDetailsHeader">
        <div className="conversationUserInfo">
          <p className="userName">
            {conversation?.user?.name}
          </p>
          <p>{conversation?.user?.email}</p>
          <p>{conversation?.hubName} - Hub ID: {conversation?.hub}</p>
          {ticketURL && (
              <div className="ticketURL">
                <Button
                  type="link"
                  href={ticketURL}
                  target="_blank"
                  icon='pg-arrow-top-right-on-square'
                  onClick={() => handleOpenZendeskTicket(ticketURL)}
                >
                  Zendesk ticket
                </Button>
              </div>
            )}
        </div>
        <div className="rightSection">
        {tags?.length > 0 && (
            <div className="tagsContainer">
              {tags?.map(tag => (
                <Chip key={tag} title={tag} size="xsmall">
                  {tag}
                </Chip>
              ))}
            </div>
          )}
          <IconButton
            type='tertiary'
            icon='pg-flag-o'
            style={{
              backgroundColor: isConversationFlagged ? 'black' : undefined,
              color: isConversationFlagged ? 'white' : undefined,
              width: '52px',
            }}
            useIconFill={isConversationFlagged}
            onClick={() => handleFlagToggle(sessionAddress, !isConversationFlagged)}
            aria-checked={isConversationFlagged}
            role="switch"
            aria-label={isConversationFlagged ? 'Unflag conversation' : 'Flag conversation'}
          />
        </div>
      </div>
        <div className="supportWidgetMessages">
        <p className="sessionDate">{moment(conversation?.startedAt).format('DD/MM/YYYY')}</p>
        {conversation?.messages?.length > 0 ? (
          conversation.messages.map(message => {
            const showSupportRequestCta =
              message.messageType !== 'USER_MESSAGE' &&
              message.messageType !== 'FEEDBACK_COLLECTED' &&
              messageContainsSupportRequestCta(message.message);
            const displayMessage = showSupportRequestCta
              ? getCleanedMessageForSupportRequestCta(message.message)
              : message.message;

            const messageSources = message.sources || [];
            const helpArticleSources = messageSources.filter(source => source.sourceType === "HELP_ARTICLE") || [];

            return (
              <div
                className={
                  message.messageType !== 'USER_MESSAGE' &&
                  message.messageType !== 'FEEDBACK_COLLECTED'
                    ? 'chatMessageResponse'
                    : 'chatMessage'
                }
                key={
                  message.messageId ||
                  `${message.createdAt?.toString()}-${uuid()}`
                }
                id={message.messageId}
              >
                <div
                  className={clsx('messageContainer', {
                    helpArticle: !!message.sources.length,
                  })}
                >
                  <div className="messageBody">
                    {message.messageType !== 'FEEDBACK_COLLECTED' ? (
                      <>
                        <RichText
                          text={displayMessage}
                          extraTags={['a']}
                          attrsForExtraTags={['target']}
                        />
                        {showSupportRequestCta && (
                          <div className="supportRequestCta">
                            {/* Read-only submit support request CTA for admins */}
                            <Button
                              type="primary"
                              disabled
                              className="supportRequestCtaButton"
                              aria-label="Submit a support request"
                            >
                              Submit a support request
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <img
                            src="https://cdn.peoplegrove.com/icons/star-o.svg"
                            height={16}
                            width={16}
                            alt="feedback score"
                          />
                          <span>{message.metadata?.feedbackScore}</span>
                        </div>
                        {message.metadata?.feedbackComment && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              margin: '8px 0',
                            }}
                          >
                            <img
                              src="https://cdn.peoplegrove.com/admin/1689334824014pg-chat-bubble-bottom-center-text-o.svg"
                              height={16}
                              width={16}
                              alt="feedback comment"
                            />
                            <span>{message.metadata?.feedbackComment}</span>
                          </div>
                        )}
                      </>
                    )}
                    {message.createdAt && (
                      <time
                        className="messageSentAt"
                        dateTime={moment(message.createdAt).toISOString()}
                      >
                        {moment(message.createdAt).format('hh:mm A')}
                      </time>
                    )}
                  </div>
                  {message.requiresFeedback && (
                    <div
                      className="responseFeedback"
                      role="group"
                      aria-labelledby={`feedback-label-${message.messageId}`}
                    >
                      <span id={`feedback-label-${message.messageId}`}>
                        Was this helpful?
                      </span>
                      <img
                        src={
                          iconsMap[
                            `pg-thumbs-up${message.isHelpful === true ? '' : '-o'}`
                          ]
                        }
                        alt="thumbs-up"
                      />
                      <img
                        src={
                          iconsMap[
                            `pg-thumbs-down${message.isHelpful === false ? '' : '-o'}`
                          ]
                        }
                        alt="thumbs-down"
                      />
                    </div>
                  )}
                </div>
                {helpArticleSources.length > 0 && (
                  <div className="helpArticleSource">
                    <div>Help Article Source:</div>
                    {helpArticleSources.map(({ sourceUrl, sourceType }, index) => (
                      <a
                        key={`${message.messageId || 'message'}-source-${sourceUrl}`}
                        href={sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginRight: '8px' }}
                      >
                        Link {index + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="noMessagesFound">User directly logged a ticket.</p>
        )}
          {conversationSummary &&
            conversationSummary !== 'ERROR_CONVERSATION_SUMMARY' &&
            conversation.sessionStatus !== 'active' && (
              <div className="converstationSummary">
                <div className="summaryTitle">Conversation Summary</div>
                <p>{conversationSummary}</p>
              </div>
            )}
        </div>
      </div>
  );
};

ConversationDetails.propTypes = {
  sessionAddress: PropTypes.string.isRequired,
};

export default ConversationDetails;
